import React, { useState } from 'react';
import {
  Dialog, AppBar, Toolbar, IconButton, Typography, Slide, Box,
  Autocomplete, TextField, Card, Chip, Stack, Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import catData from '../utils/catData';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function parseDate(str) {
  const [d, m, y] = str.split('/');
  return new Date(+y, +m - 1, +d);
}

function getStatus(expiryStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = parseDate(expiryStr);
  const msLeft = expiry - today;
  const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));

  if (daysLeft < 0) {
    return { label: 'פג תוקף', color: 'error', icon: <CancelOutlinedIcon />, daysLeft };
  }
  if (daysLeft <= 180) {
    return { label: 'עומד לפוג בקרוב', color: 'warning', icon: <WarningAmberIcon />, daysLeft };
  }
  return { label: 'בתוקף', color: 'success', icon: <CheckCircleOutlineIcon />, daysLeft };
}

const batchOptions = catData.map((item) => item.batch);

const CatCheck = ({ open, handleClose }) => {
  const [inputValue, setInputValue] = useState('');
  const [selected, setSelected] = useState(null);

  const handleSelect = (_, newValue) => {
    const found = catData.find((item) => item.batch === newValue);
    setSelected(found || null);
  };

  const status = selected ? getStatus(selected.expiry) : null;

  const daysText = (daysLeft) => {
    if (daysLeft < 0) return `פג לפני ${Math.abs(daysLeft)} ימים`;
    if (daysLeft === 0) return 'פג היום';
    return `${daysLeft} ימים נותרו`;
  };

  return (
    <Dialog fullScreen open={open} onClose={handleClose} TransitionComponent={Transition}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
            בדיקת תוקף CAT
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3, maxWidth: 500, mx: 'auto', mt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          הקלד את מספר האצווה של ה-CAT לבדיקת תוקפו
        </Typography>

        <Autocomplete
          freeSolo
          options={batchOptions}
          inputValue={inputValue}
          onInputChange={(_, val) => {
            setInputValue(val);
            if (!val) setSelected(null);
          }}
          onChange={handleSelect}
          filterOptions={(opts, { inputValue: iv }) =>
            iv.length < 2 ? [] : opts.filter((o) => o.includes(iv)).slice(0, 20)
          }
          renderInput={(params) => (
            <TextField {...params} label="מספר אצווה / סדרה" fullWidth autoFocus />
          )}
        />

        {selected && status && (
          <Card
            sx={{
              mt: 3,
              p: 2.5,
              border: 2,
              borderColor:
                status.color === 'success'
                  ? 'success.main'
                  : status.color === 'warning'
                  ? 'warning.main'
                  : 'error.main',
              borderRadius: 2,
            }}
          >
            <Stack spacing={1.5}>
              <Stack spacing={0.75}>
                <Chip
                  icon={status.icon}
                  label={status.label}
                  color={status.color}
                  variant="filled"
                  sx={{ fontWeight: 'bold', fontSize: '0.9rem', alignSelf: 'flex-start' }}
                />
                <Typography variant="h6" fontWeight="bold" noWrap>
                  אצווה: {selected.batch}
                </Typography>
              </Stack>

              <Divider />

              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">תאריך ייצור:</Typography>
                <Typography fontWeight="medium">{selected.created}</Typography>
              </Stack>

              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">תאריך תפוגה:</Typography>
                <Typography fontWeight="medium">{selected.expiry}</Typography>
              </Stack>

              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">ימים עד לפג"ת:</Typography>
                <Typography
                  fontWeight="bold"
                  color={
                    status.color === 'success'
                      ? 'success.main'
                      : status.color === 'warning'
                      ? 'warning.main'
                      : 'error.main'
                  }
                >
                  {daysText(status.daysLeft)}
                </Typography>
              </Stack>
            </Stack>
          </Card>
        )}

        {inputValue.length >= 2 && !selected && (
          <Card sx={{ mt: 3, p: 2, bgcolor: 'grey.100' }}>
            <Typography color="text.secondary" textAlign="center">
              אצווה לא נמצאה במאגר
            </Typography>
          </Card>
        )}

        <Box sx={{ mt: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: 0.3 }}>
          <img
            src="/cat.svg"
            alt="CAT tourniquet"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            style={{ width: '65%', maxWidth: 300, objectFit: 'contain' }}
          />
        </Box>
      </Box>
    </Dialog>
  );
};

export default CatCheck;
